import type { CandidateStatus } from '@eduvy/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Icon, type IconName } from '../components/icons';
import { Avatar, Badge, BarButton, Button, Callout, Checkbox, CopyButton, Empty, ErrorNote, IconButton, Input, Loading, Meta, Modal, PageHeader, ProgressBar, Segmented, SelectionBar, Select, Table, TD, TH, TR, Tabs, Textarea, cx } from '../components/ui';
import { api, type CandidateRow, type Job, type Stage } from '../lib/api';
import { CANDIDATE_STATUS, CHANNEL_LABEL, INTERVIEW_STATUS, OUTREACH_LABEL, VERDICT, formatDateTime, formatPhone, scoreText, whatsappLink } from '../lib/format';
import { useNow } from '../lib/hooks';
import { COLUMNS, groupByColumn, matchesSearch, progressHint, whyLines, type ColumnDef, type ColumnKey } from '../lib/pipeline';
import { ImportDialog } from './ImportDialog';

const FILTERS: Array<{ key: string; label: string; statuses: CandidateStatus[] | null }> = [
  { key: 'all', label: 'All', statuses: null },
  ...COLUMNS.map((c) => ({ key: c.key, label: c.label, statuses: c.statuses })),
];

/** Cards rendered per column before "Show more"; keeps a 500-lead board light without virtualisation. */
const PAGE = 40;

type View = 'board' | 'table';

function readView(): View {
  try {
    return localStorage.getItem('eduvy:pipeline-view') === 'table' ? 'table' : 'board';
  } catch {
    return 'board';
  }
}

const OUTREACH_ICON: Record<Job['outreachMode'], IconName> = { WEB_LINK: 'globe', PHONE_CALL: 'phone', PHONE_THEN_WEB: 'send' };

export function JobPipelinePage() {
  const { jobId } = useParams() as { jobId: string };
  const qc = useQueryClient();
  const job = useQuery({ queryKey: ['job', jobId], queryFn: () => api.get<Job>(`/api/jobs/${jobId}`) });
  const candidates = useQuery({
    queryKey: ['candidates', jobId],
    queryFn: () => api.get<CandidateRow[]>(`/api/jobs/${jobId}/candidates`),
    refetchInterval: 8_000,
  });
  const [view, setViewState] = useState<View>(readView);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [bulk, setBulk] = useState<BulkKind | null>(null);

  const setView = (v: View) => {
    setViewState(v);
    try {
      localStorage.setItem('eduvy:pipeline-view', v);
    } catch {
      /* private mode: the choice just isn't remembered */
    }
  };

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['candidates', jobId] });
    void qc.invalidateQueries({ queryKey: ['job', jobId] });
  };

  const searched = useMemo(() => (candidates.data ?? []).filter((c) => matchesSearch(c, search)), [candidates.data, search]);
  const tableRows = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter)!;
    return searched.filter((c) => !f.statuses || f.statuses.includes(c.status));
  }, [searched, filter]);
  const columns = useMemo(() => groupByColumn(searched), [searched]);
  const hasLive = searched.some((c) => c.status === 'IN_INTERVIEW');
  const now = useNow(hasLive && view === 'board' ? 1000 : 30_000);

  if (job.isLoading) return <Loading />;
  if (!job.data) return <ErrorNote error={job.error ?? 'Job not found'} />;
  const j = job.data;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const setMany = (ids: string[], on: boolean) => {
    const next = new Set(selected);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    setSelected(next);
  };
  const active = j.status === 'ACTIVE';

  return (
    <div>
      <PageHeader
        back={{ to: '/jobs', label: 'Jobs' }}
        title={j.title}
        aside={<StatusPill status={j.status} />}
        meta={
          <>
            <Meta icon="pin">{j.location}</Meta>
            <Meta icon="building">{j.workMode}</Meta>
            <Meta icon="clock">{j.interviewMinutes}-minute AI interview</Meta>
            <Meta icon={OUTREACH_ICON[j.outreachMode]}>{OUTREACH_LABEL[j.outreachMode] ?? j.outreachMode}</Meta>
          </>
        }
        actions={
          <>
            {j.config.apply?.enabled && j.applyUrl && (
              <CopyButton text={j.applyUrl} size="md" variant="secondary" icon="link">
                Apply link
              </CopyButton>
            )}
            <Button icon="edit" to={`/jobs/${jobId}/edit`}>
              Edit rubric
            </Button>
            <Button
              icon="download"
              onClick={() => {
                window.location.href = `/api/jobs/${jobId}/export.csv`;
              }}
            >
              Export
            </Button>
            <Button icon="upload" onClick={() => setImportOpen(true)}>
              Import leads
            </Button>
            <Button variant="primary" icon="play" disabled={selected.size === 0 || !active} onClick={() => setStartOpen(true)} title={!active ? 'Set the job to active first' : selected.size === 0 ? 'Select candidates first' : undefined}>
              Start interviews{selected.size > 0 ? ` (${selected.size})` : ''}
            </Button>
          </>
        }
      />

      {!active && (
        <ActivateBanner
          jobId={jobId}
          status={j.status}
          onDone={() => {
            refresh();
            void qc.invalidateQueries({ queryKey: ['jobs'] });
          }}
        />
      )}

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Segmented<View>
          label="View"
          value={view}
          onChange={setView}
          options={[
            { value: 'board', label: 'Board', icon: 'columns' },
            { value: 'table', label: 'Table', icon: 'list' },
          ]}
        />
        <div className="w-full sm:w-72">
          <Input type="search" leading={<Icon name="search" className="h-4 w-4" />} placeholder="Search name, phone, email, sector" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search candidates" />
        </div>
        <span className="ml-auto font-mono text-xs text-muted-fg">
          {candidates.data?.length ?? 0} leads{search && ` · ${searched.length} match`}
        </span>
      </div>

      <ErrorNote error={candidates.error} />
      {candidates.isLoading ? (
        <Loading />
      ) : candidates.data?.length === 0 ? (
        <Empty
          icon="upload"
          title="No leads yet"
          action={
            <Button variant="primary" icon="upload" onClick={() => setImportOpen(true)}>
              Import a lead sheet
            </Button>
          }
        >
          Upload the sheet (xlsx or csv) or paste its rows. Every lead is cleaned, de-duplicated and pre-screened on import.
        </Empty>
      ) : view === 'board' ? (
        <Board columns={columns} job={j} now={now} selected={selected} onToggle={toggle} onSetMany={setMany} />
      ) : (
        <>
          <div className="mb-4">
            <Tabs
              label="Filter"
              value={filter}
              onChange={setFilter}
              options={FILTERS.map((f) => ({ value: f.key, label: f.label, count: f.statuses ? searched.filter((c) => f.statuses!.includes(c.status)).length : searched.length }))}
            />
          </div>
          <CandidateTable rows={tableRows} job={j} selected={selected} onToggle={toggle} onSetMany={setMany} />
        </>
      )}

      <SelectionBar count={selected.size} noun={selected.size === 1 ? 'candidate' : 'candidates'} onClear={() => setSelected(new Set())}>
        <BarButton primary icon="play" onClick={() => active && setStartOpen(true)}>
          Start interviews
        </BarButton>
        {(Object.keys(BULK) as BulkKind[]).map((k) => (
          <BarButton key={k} danger={k === 'REJECT'} onClick={() => setBulk(k)}>
            {BULK[k].button}
          </BarButton>
        ))}
      </SelectionBar>

      <ImportDialog jobId={jobId} open={importOpen} onClose={() => setImportOpen(false)} onImported={refresh} />
      <BulkDecisionDialog
        job={j}
        kind={bulk}
        ids={[...selected]}
        onClose={() => setBulk(null)}
        onDone={() => {
          setSelected(new Set());
          refresh();
        }}
      />
      <StartDialog
        job={j}
        ids={[...selected]}
        open={startOpen}
        onClose={() => setStartOpen(false)}
        onStarted={() => {
          setSelected(new Set());
          refresh();
        }}
      />
    </div>
  );
}

/** Interviews start only on an active job: activate it here rather than in the rubric form. */
function ActivateBanner({ jobId, status, onDone }: { jobId: string; status: Job['status']; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const activate = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/jobs/${jobId}/status`, { status: 'ACTIVE' });
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-5 space-y-2">
      <Callout
        tone="warning"
        action={
          <Button size="sm" variant="primary" icon="zap" onClick={activate} loading={busy}>
            Set it to active
          </Button>
        }
      >
        This job is {status.toLowerCase()}, so interviews cannot start yet.
      </Callout>
      <ErrorNote error={error} />
    </div>
  );
}

function StatusPill({ status }: { status: Job['status'] }) {
  return (
    <Badge tone={status === 'ACTIVE' ? 'green' : status === 'DRAFT' ? 'slate' : 'red'} dot>
      {status === 'ACTIVE' ? 'Active' : status === 'DRAFT' ? 'Draft' : 'Closed'}
    </Badge>
  );
}

function SmallCheck({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={cx('inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] ring-1 ring-inset transition', checked ? 'bg-brand-gradient ring-transparent' : 'bg-surface ring-border-strong hover:ring-fg/40')}
    >
      {checked && <Icon name="check" className="h-2.5 w-2.5 text-white" strokeWidth={3.5} />}
    </button>
  );
}

// ---- board -----------------------------------------------------------------------------------

function Board({
  columns,
  job,
  now,
  selected,
  onToggle,
  onSetMany,
}: {
  columns: Record<ColumnKey, CandidateRow[]>;
  job: Job;
  now: number;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onSetMany: (ids: string[], on: boolean) => void;
}) {
  const [decidedOpen, setDecidedOpen] = useState(false);
  return (
    // Phones: the columns stack as sections, each as tall as its cards; wider screens: the side-by-side board.
    <div className="scrollbar-thin flex flex-col gap-3 pb-4 md:-mx-1 md:flex-row md:overflow-x-auto md:px-1">
      {COLUMNS.map((col) =>
        col.key === 'decided' && !decidedOpen ? (
          <button
            key={col.key}
            onClick={() => setDecidedOpen(true)}
            className="flex w-full shrink-0 items-center gap-3 rounded-lg bg-surface-2 px-3 py-3 text-xs font-medium text-muted-fg ring-1 ring-border transition hover:text-accent md:w-12 md:flex-col md:px-0 md:py-4"
            title="Show decided candidates"
          >
            <span className="font-mono text-sm text-fg">{columns.decided.length}</span>
            <span className="md:[writing-mode:vertical-rl]">Decided · selected, shortlisted, rejected, on hold</span>
          </button>
        ) : (
          <Column
            key={col.key}
            col={col}
            rows={columns[col.key]}
            job={job}
            now={now}
            selected={selected}
            onToggle={onToggle}
            onSetMany={onSetMany}
            onCollapse={col.key === 'decided' ? () => setDecidedOpen(false) : undefined}
          />
        ),
      )}
    </div>
  );
}

function Column({
  col,
  rows,
  job,
  now,
  selected,
  onToggle,
  onSetMany,
  onCollapse,
}: {
  col: ColumnDef;
  rows: CandidateRow[];
  job: Job;
  now: number;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onSetMany: (ids: string[], on: boolean) => void;
  onCollapse?: (() => void) | undefined;
}) {
  const [limit, setLimit] = useState(PAGE);
  const shown = rows.slice(0, limit);
  const allOn = rows.length > 0 && rows.every((r) => selected.has(r.id));
  return (
    <section className="flex w-full shrink-0 flex-col rounded-lg bg-surface-2 ring-1 ring-border md:w-[290px]" aria-label={col.label}>
      <header className="flex items-center gap-2.5 px-3 pb-2 pt-3">
        {rows.length > 0 ? <SmallCheck checked={allOn} onChange={() => onSetMany(rows.map((r) => r.id), !allOn)} label={`Select all in ${col.label}`} /> : <span className="h-4 w-4" />}
        <span className="h-2 w-2 rounded-full" style={{ background: col.accent, boxShadow: `0 0 8px ${col.accent}` }} />
        <h2 className="min-w-0 flex-1 truncate text-[13px] font-semibold text-fg">{col.label}</h2>
        <span className="rounded-full bg-surface px-1.5 py-px font-mono text-[10px] text-muted-fg ring-1 ring-border">{rows.length}</span>
        {onCollapse && <IconButton label="Collapse" icon="x" size="sm" onClick={onCollapse} className="-mr-1.5" />}
      </header>
      <div className="scrollbar-thin flex flex-col gap-2 px-2 pb-2 md:max-h-[calc(100vh-300px)] md:min-h-24 md:overflow-y-auto">
        {shown.map((c) => (
          <BoardCard key={c.id} c={c} col={col.key} job={job} now={now} checked={selected.has(c.id)} onToggle={() => onToggle(c.id)} />
        ))}
        {rows.length === 0 && <p className="hidden px-2 py-8 text-center text-xs text-faint md:block">Nobody here</p>}
        {rows.length > limit && (
          <Button size="xs" variant="ghost" onClick={() => setLimit(limit + PAGE * 2)}>
            Show {Math.min(rows.length - limit, PAGE * 2)} more of {rows.length - limit}
          </Button>
        )}
      </div>
    </section>
  );
}

function ScorePill({ score }: { score: number }) {
  return <span className={cx('display text-[22px] font-medium leading-none tabular', scoreText(score))}>{score}</span>;
}

function BoardCard({ c, col, job, now, checked, onToggle }: { c: CandidateRow; col: ColumnKey; job: Job; now: number; checked: boolean; onToggle: () => void }) {
  const hint = progressHint(c, job, now);
  const flags = c.preScreen?.flags ?? [];
  const status = CANDIDATE_STATUS[c.status];
  return (
    <article
      className={cx(
        'group relative rounded-md bg-surface p-3 text-sm shadow-[0_1px_2px_rgba(12,9,22,0.04)] ring-1 transition duration-150 hover:shadow-card',
        checked ? 'ring-2 ring-accent' : 'ring-border hover:ring-border-strong',
        col === 'review' && 'border-l-[3px] border-warning',
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5">
          <SmallCheck checked={checked} onChange={onToggle} label={`Select ${c.name}`} />
        </span>
        <div className="min-w-0 flex-1">
          <Link to={`/candidates/${c.id}`} className="block truncate font-semibold text-fg transition hover:text-accent">
            {c.name}
          </Link>
          <p className="truncate text-xs text-muted-fg">{[c.experienceRaw && `${c.experienceRaw} yrs`, c.sector].filter(Boolean).join(' · ') || formatPhone(c.phoneE164)}</p>
        </div>
        {c.score && (col === 'qualified' || col === 'review' || col === 'out' || col === 'decided') && <ScorePill score={c.score.overallScore} />}
      </div>

      {col === 'qualified' && c.score && (
        <>
          <ProgressBar value={c.score.overallScore} tone="auto" size="sm" className="mt-2.5" />
          {c.score.summary && <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-fg">{c.score.summary}</p>}
        </>
      )}

      {col === 'review' && (
        <p className="mt-2 line-clamp-2 rounded-[8px] bg-warning-soft px-2 py-1.5 text-xs text-warning">{c.score?.reasons?.[0] ?? c.score?.summary ?? flags[0] ?? 'Flagged for a human look'}</p>
      )}

      {(col === 'out' || col === 'decided') && (
        <div className="mt-2">
          <Badge tone={status.tone} size="xs">
            {status.label}
          </Badge>
        </div>
      )}

      {col === 'assessment' && c.skills && (
        <p className="mt-2 text-xs text-muted-fg">
          Skills <span className={cx('font-mono font-semibold', scoreText(c.skills.overallScore))}>{c.skills.overallScore}</span> · {VERDICT[c.skills.verdict].label.toLowerCase()}
        </p>
      )}

      {hint && (
        <p className={cx('mt-2 flex items-center gap-1.5 text-xs', hint.kind === 'live' ? 'font-semibold text-pink' : hint.kind === 'scheduled' ? 'text-purple dark:text-accent' : 'text-muted-fg')}>
          {hint.kind === 'live' && (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-pink opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-pink" />
            </span>
          )}
          {hint.kind === 'scheduled' && <Icon name="calendar" className="h-3.5 w-3.5" />}
          <span className={hint.kind === 'live' ? 'font-mono' : undefined}>{hint.text}</span>
        </p>
      )}

      {col === 'new' && flags.length > 0 && (
        <p className="mt-2 inline-flex items-center gap-1 text-xs text-warning" title={flags.join('\n')}>
          <Icon name="flag" className="h-3 w-3" /> {flags.length} import flag{flags.length > 1 ? 's' : ''}
        </p>
      )}
      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-border pt-2">
        {c.latest?.link ? <LinkActions c={c} job={job} link={c.latest.link} /> : <span className="text-[11px] text-faint">{formatPhone(c.phoneE164)}</span>}
        <Link to={`/candidates/${c.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-accent opacity-0 transition group-hover:opacity-100">
          Open <Icon name="arrowRight" className="h-3 w-3" />
        </Link>
      </div>
    </article>
  );
}

function LinkActions({ c, job, link, className }: { c: CandidateRow; job: Job; link: string; className?: string }) {
  return (
    <div className={cx('flex items-center gap-1', className)}>
      <CopyButton text={link} />
      {c.phoneE164 && (
        <a
          className="inline-flex h-7 items-center gap-1.5 rounded-[8px] px-2 text-xs font-semibold text-success transition hover:bg-success-soft"
          target="_blank"
          rel="noreferrer"
          href={whatsappLink(c.phoneE164, `Hi ${c.name.split(' ')[0]}, thank you for applying for the ${job.title} role at ${job.config.companyName}. Please take your ${job.interviewMinutes}-minute AI interview here: ${link}`)}
        >
          <Icon name="whatsapp" className="h-3.5 w-3.5" />
          WhatsApp
        </a>
      )}
    </div>
  );
}

// ---- table -----------------------------------------------------------------------------------

function CandidateTable({
  rows,
  job,
  selected,
  onToggle,
  onSetMany,
}: {
  rows: CandidateRow[];
  job: Job;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onSetMany: (ids: string[], on: boolean) => void;
}) {
  const [limit, setLimit] = useState(200);
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  return (
    <Table minWidth="1100px">
      <thead className="bg-surface-2">
        <tr>
          <th className={cx(TH, 'w-12')}>
            <SmallCheck checked={allSelected} onChange={() => onSetMany(rows.map((r) => r.id), !allSelected)} label="Select all" />
          </th>
          <th className={TH}>Candidate</th>
          <th className={TH}>Sheet</th>
          <th className={TH}>Status</th>
          <th className={cx(TH, 'w-64')}>Why</th>
          <th className={TH}>Latest attempt</th>
          <th className={TH}>Booked for</th>
          <th className={cx(TH, 'w-44')}>Score</th>
          <th className={TH} />
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, limit).map((c) => (
          <CandidateTableRow key={c.id} c={c} job={job} checked={selected.has(c.id)} onToggle={() => onToggle(c.id)} />
        ))}
      </tbody>
      {rows.length === 0 && (
        <tfoot>
          <tr>
            <td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-fg">
              No candidates match this filter.
            </td>
          </tr>
        </tfoot>
      )}
      {rows.length > limit && (
        <tfoot>
          <tr>
            <td colSpan={9} className="border-t border-border p-2 text-center">
              <Button size="sm" variant="ghost" onClick={() => setLimit(limit + 200)}>
                Show {Math.min(200, rows.length - limit)} more of {rows.length - limit}
              </Button>
            </td>
          </tr>
        </tfoot>
      )}
    </Table>
  );
}

const STAGE_SHORT: Record<Stage, string> = { SCREENING: 'AI interview', ASSESSMENT: 'Skills assessment', RECRUITER: 'Recruiter interview' };

function CandidateTableRow({ c, job, checked, onToggle }: { c: CandidateRow; job: Job; checked: boolean; onToggle: () => void }) {
  const status = CANDIDATE_STATUS[c.status];
  const flags = c.preScreen?.flags ?? [];
  const link = c.latest?.link;
  const why = whyLines(c);
  const open = c.latest && ['PENDING', 'INVITED', 'RINGING', 'IN_PROGRESS'].includes(c.latest.status);
  const booked = open && c.latest?.scheduledAt ? { at: c.latest.scheduledAt, stage: c.latest.stage } : null;
  return (
    <tr className={cx(TR, checked && 'bg-accent-soft/60')}>
      <td className={TD}>
        <SmallCheck checked={checked} onChange={onToggle} label={`Select ${c.name}`} />
      </td>
      <td className={TD}>
        <div className="flex items-center gap-2.5">
          <Avatar name={c.name} size="sm" />
          <div className="min-w-0">
            <Link to={`/candidates/${c.id}`} className="font-semibold text-fg hover:text-accent">
              {c.name}
            </Link>
            <div className="text-xs text-muted-fg">
              {formatPhone(c.phoneE164)}
              {c.email && <> · {c.email}</>}
            </div>
          </div>
        </div>
      </td>
      <td className={cx(TD, 'text-xs text-muted-fg')}>
        <div>{c.experienceRaw ? `${c.experienceRaw} yrs` : '—'}</div>
        <div className="max-w-[200px] truncate" title={c.sector ?? ''}>
          {c.sector}
        </div>
        {flags.length > 0 && (
          <div className="mt-0.5 inline-flex items-center gap-1 text-warning" title={flags.join('\n')}>
            <Icon name="flag" className="h-3 w-3" /> {flags.length} flag{flags.length > 1 ? 's' : ''}
          </div>
        )}
      </td>
      <td className={TD}>
        <Badge tone={status.tone} dot>
          {status.label}
        </Badge>
      </td>
      <td className={cx(TD, 'text-xs text-muted-fg')}>
        {why.length ? (
          <ul className="space-y-0.5" title={why.join('\n')}>
            {why.slice(0, 2).map((r, i) => (
              <li key={i} className="line-clamp-2">
                {r}
              </li>
            ))}
          </ul>
        ) : (
          '—'
        )}
      </td>
      <td className={cx(TD, 'text-xs text-muted-fg')}>
        {c.latest ? (
          <>
            <div>
              {CHANNEL_LABEL[c.latest.channel] ?? c.latest.channel} #{c.latest.attemptNo} · <span className="font-medium text-fg">{INTERVIEW_STATUS[c.latest.status].label}</span>
            </div>
            {link && <LinkActions c={c} job={job} link={link} className="mt-1 -ml-2" />}
          </>
        ) : (
          '—'
        )}
      </td>
      <td className={cx(TD, 'text-xs')}>
        {booked ? (
          <>
            <div className="font-medium text-fg">{formatDateTime(booked.at)}</div>
            <div className="text-muted-fg">{STAGE_SHORT[booked.stage]}</div>
          </>
        ) : (
          <span className="text-muted-fg">—</span>
        )}
      </td>
      <td className={TD}>
        {c.score ? (
          <div>
            <div className="mb-1.5 flex items-center justify-between text-xs">
              <span className={cx('font-mono font-semibold', scoreText(c.score.overallScore))}>{c.score.overallScore}</span>
              <Badge tone={VERDICT[c.score.verdict].tone} size="xs">
                {VERDICT[c.score.verdict].label}
              </Badge>
            </div>
            <ProgressBar value={c.score.overallScore} tone="auto" size="sm" />
          </div>
        ) : (
          <span className="text-xs text-muted-fg">—</span>
        )}
        {c.skills && (
          <div className="mt-2 text-xs text-muted-fg">
            Skills <span className={cx('font-mono font-semibold', scoreText(c.skills.overallScore))}>{c.skills.overallScore}</span> · {VERDICT[c.skills.verdict].label.toLowerCase()}
          </div>
        )}
      </td>
      <td className={cx(TD, 'text-right')}>
        <Button size="xs" variant="ghost" iconRight="arrowRight" to={`/candidates/${c.id}`}>
          Open
        </Button>
      </td>
    </tr>
  );
}

// ---- bulk decisions --------------------------------------------------------------------------

type BulkKind = 'SHORTLIST' | 'ADVANCE' | 'HOLD' | 'REJECT';

const BULK: Record<BulkKind, { button: string; title: string; help: string }> = {
  SHORTLIST: {
    button: 'Shortlist',
    title: 'Shortlist',
    help: 'Candidates with a first-interview result move on: when the job has a skills assessment, each gets their own tasks and the assessment link with the rules. Anyone else in the selection is skipped.',
  },
  ADVANCE: {
    button: 'Invite to recruiter interview',
    title: 'Invite to the recruiter interview',
    help: "Candidates who finished the skills assessment get a link to book a time from the recruiters' availability. Anyone else is skipped.",
  },
  HOLD: { button: 'Hold', title: 'Put on hold', help: 'Any link still open is withdrawn.' },
  REJECT: { button: 'Reject', title: 'Reject', help: 'Any link still open is withdrawn.' },
};

function delayWords(hours: number): string {
  return hours === 0 ? 'right away' : hours === 24 ? 'after 24 hours' : `after ${hours} hours`;
}

function Skipped({ skipped }: { skipped: Array<{ name: string; reason: string }> }) {
  if (skipped.length === 0) return null;
  return (
    <div className="rounded-md bg-surface-2 p-3 ring-1 ring-border">
      <p className="mb-1 text-xs font-semibold text-muted-fg">Skipped</p>
      <ul className="space-y-0.5 text-sm text-muted-fg">
        {skipped.map((x, i) => (
          <li key={i}>
            <span className="font-medium text-fg">{x.name}</span>: {x.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

function BulkDecisionDialog({ job, kind, ids, onClose, onDone }: { job: Job; kind: BulkKind | null; ids: string[]; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<{ done: number; skipped: Array<{ name: string; reason: string }> } | null>(null);
  const reject = job.config.messages?.reject ?? { send: true, delayHours: 24 };
  const sendEmail = notify ?? reject.send;

  const close = () => {
    setResult(null);
    setError(null);
    setReason('');
    setNotify(null);
    onClose();
  };
  const submit = async () => {
    if (!kind) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ done: number; skipped: Array<{ name: string; reason: string }> }>(`/api/jobs/${job.id}/decisions`, {
        kind,
        candidateIds: ids,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(kind === 'REJECT' ? { notify: sendEmail } : {}),
      });
      setResult(r);
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const d = kind ? BULK[kind] : null;
  return (
    <Modal
      open={kind !== null}
      onClose={close}
      title={d ? `${d.title}: ${ids.length} candidate${ids.length === 1 ? '' : 's'}` : ''}
      description={d?.help}
      footer={
        result ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant={kind === 'REJECT' ? 'danger' : 'primary'} loading={busy} onClick={() => void submit()}>
              {d?.title}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3 text-sm">
          <Callout tone="success">
            <strong>{result.done}</strong> candidate{result.done === 1 ? '' : 's'} updated.
          </Callout>
          <Skipped skipped={result.skipped} />
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          {kind === 'REJECT' && (
            <Checkbox checked={sendEmail} onChange={setNotify} hint="Only candidates with an email address. The wording is in the job's settings, under Emails.">
              Email each of them a polite rejection, {delayWords(reject.delayHours)}
            </Checkbox>
          )}
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-semibold text-fg">Reason</span>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional · kept in the audit trail, not sent" />
          </label>
          <ErrorNote error={error} />
        </div>
      )}
    </Modal>
  );
}

// ---- start dialog ----------------------------------------------------------------------------

function StartDialog({ job, ids, open, onClose, onStarted }: { job: Job; ids: string[]; open: boolean; onClose: () => void; onStarted: () => void }) {
  const [mode, setMode] = useState(job.outreachMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<{ queued: number; skipped: Array<{ name: string; reason: string }> } | null>(null);

  const close = () => {
    setResult(null);
    setError(null);
    onClose();
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ queued: number; skipped: Array<{ name: string; reason: string }> }>(`/api/jobs/${job.id}/interviews/start`, { candidateIds: ids, mode });
      setResult(r);
      onStarted();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Start interviews for ${ids.length} candidate${ids.length === 1 ? '' : 's'}`}
      description="Anyone already mid-interview is skipped. Starting again for a candidate cancels their open link or scheduled call."
      footer={
        result ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="primary" icon="play" onClick={start} loading={busy}>
              Start
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3 text-sm">
          <Callout tone="success">
            <strong>{result.queued}</strong> interview{result.queued === 1 ? '' : 's'} queued. Web links go out by email right away; calls are placed within calling hours.
          </Callout>
          <Skipped skipped={result.skipped} />
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block text-sm">
            <span className="mb-1.5 block text-[13px] font-semibold text-fg">How to reach them</span>
            <Select value={mode} onChange={(e) => setMode(e.target.value as Job['outreachMode'])}>
              <option value="WEB_LINK">Web link by email</option>
              <option value="PHONE_CALL">Phone call</option>
              <option value="PHONE_THEN_WEB">Phone call, then web link</option>
            </Select>
          </label>
          <ErrorNote error={error} />
        </div>
      )}
    </Modal>
  );
}
